import { useEffect, useState } from "react";
import axios from "axios";
import { Instagram as InstagramIcon, Play, Images } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { BUSINESS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Instagram = () => {
  const [feed, setFeed] = useState({ connected: false, data: [] });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/instagram/feed?limit=6`);
        if (mounted) setFeed(res.data);
      } catch {
        if (mounted) setFeed({ connected: false, data: [] });
      }
    };
    load();
    const timer = setInterval(load, 10 * 60 * 1000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  const posts = feed.connected ? feed.data : [];

  return (
    <section id="instagram" data-testid="instagram-section" className="py-20 md:py-32 bg-royal relative grain overflow-hidden scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
        <SectionHeading
          chapter="06"
          overline="Instagram"
          title="Follow Our Journey"
          description="New designs, reels, behind-the-scenes moments and showroom stories — follow @jewellerkalikaprasad."
          dark
          center
        />

        {posts.length > 0 ? (
          <div data-testid="instagram-live-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 -mt-4 md:-mt-8">
            {posts.map((post, i) => {
              const isReel = post.media_type === "VIDEO";
              const src = isReel ? post.thumbnail_url : post.media_url;
              const label = post.caption ? post.caption.slice(0, 120) : "Instagram post by @jewellerkalikaprasad";
              return (
                <Reveal key={post.id} delay={0.05 * i}>
                  <a
                    data-testid={`instagram-post-${i}`}
                    href={post.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative block aspect-square overflow-hidden rounded-sm"
                    aria-label={label}
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={label}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-royal-deep flex items-center justify-center">
                        <InstagramIcon size={22} strokeWidth={1.2} className="text-gold/60" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-royal-deep/0 group-hover:bg-royal-deep/50 transition-colors duration-500 flex items-center justify-center" aria-hidden="true">
                      <InstagramIcon size={22} strokeWidth={1.5} className="text-ivory opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                    {isReel && (
                      <span className="absolute top-2.5 right-2.5 text-gold" aria-label="Instagram reel">
                        <Play size={15} strokeWidth={1.5} fill="currentColor" />
                      </span>
                    )}
                    {post.media_type === "CAROUSEL_ALBUM" && (
                      <span className="absolute top-2.5 right-2.5 text-gold" aria-label="Instagram carousel">
                        <Images size={15} strokeWidth={1.5} />
                      </span>
                    )}
                  </a>
                </Reveal>
              );
            })}
          </div>
        ) : (
          <Reveal className="-mt-4 md:-mt-8">
            <div data-testid="instagram-connecting-state" className="border border-dashed border-gold/40 rounded-sm px-8 py-14 text-center max-w-2xl mx-auto">
              <span className="inline-flex w-14 h-14 rounded-full border border-gold/50 items-center justify-center mb-5">
                <InstagramIcon size={22} strokeWidth={1.2} className="text-gold" />
              </span>
              <p className="font-serif text-2xl md:text-3xl text-ivory">Live from @jewellerkalikaprasad</p>
              <p className="mt-3 text-sm md:text-base font-light text-ivory/60 leading-relaxed max-w-md mx-auto">
                Our latest photos and reels will appear here automatically, straight from our official Instagram account.
              </p>
            </div>
          </Reveal>
        )}

        <Reveal className="mt-12 text-center">
          <a
            data-testid="instagram-follow-button"
            href={BUSINESS.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 px-8 py-3.5 border border-gold text-gold text-sm tracking-[0.12em] uppercase font-medium rounded-sm hover:bg-gold hover:text-royal-deep transition-colors duration-300"
          >
            <InstagramIcon size={16} strokeWidth={1.5} /> Follow Us on Instagram
          </a>
        </Reveal>
      </div>
    </section>
  );
};
